<template>
    <div>
        <card-header title="Contact" icon="fa-address-book"/>

        <!-- Main/Tasks/Notes only. A contact is never attached to a contact,
             so no Contacts tab here (the server rejects it as a host too). -->
        <entity-tabs v-model="tab" main-label="Contact" main-icon="fa-address-book"
                     :task-count="taskCount" :task-completed-count="taskCompleted" :note-count="noteCount"/>

        <card-body>
            <entity-tasks-pane v-show="tab === 'tasks'" :card="card" :child="child" entity-type="contact"
                               :entity-id="props.contactId" @count="taskCount = $event" @completed="taskCompleted = $event"/>
            <entity-notes-pane v-show="tab === 'notes'" :card="card" :child="child" entity-type="contact"
                               :entity-id="props.contactId" @count="noteCount = $event"/>

            <card-list v-show="tab === 'main'">
                <card-toolbar>
                    <button @click="$openCard('edit-contact', {contactId: props.contactId}, card)">
                        <i class="fas fa-pencil mr-2"/>Edit
                    </button>
                </card-toolbar>

                <subheader-list-item title="Contact" icon="fa-address-card"/>
                <data-row title="Company" v-if="contact.company">{{ contact.company }}</data-row>
                <data-row title="Name" v-if="contact.name">{{ contact.name }}</data-row>
                <data-row title="Job Title" v-if="contact.title">{{ contact.title }}</data-row>
                <data-row title="Type"><b-tag>{{ typeLabel }}</b-tag></data-row>
                <data-row title="Deleted" v-if="contact.deleted">
                    <b-tag type="is-warning">Removed from directory</b-tag>
                </data-row>

                <subheader-list-item title="Reaching them" icon="fa-envelope"/>
                <data-row title="E-Mail" v-if="contact.email"><a :href="'mailto:' + contact.email">{{ contact.email }}</a></data-row>
                <data-row title="E-Mail 2" v-if="contact.email_2"><a :href="'mailto:' + contact.email_2">{{ contact.email_2 }}</a></data-row>
                <data-row title="Phone" v-if="contact.phone"><a :href="'tel:' + contact.phone">{{ contact.phone }}</a></data-row>
                <data-row title="Phone 2" v-if="contact.phone_2"><a :href="'tel:' + contact.phone_2">{{ contact.phone_2 }}</a></data-row>
                <data-row title="Website" v-if="contact.website">
                    <a :href="contact.website" target="_blank" rel="noopener">{{ contact.website }}</a>
                </data-row>
                <data-row title="Address" v-if="contact.address">
                    <span style="white-space: pre-line">{{ contact.address }}</span>
                </data-row>
                <data-row title="City" v-if="contact.city">{{ contact.city }}</data-row>
                <data-row title="Country" v-if="contact.country">{{ contact.country }}</data-row>
                <data-row title="Notes" v-if="contact.notes">
                    <span style="white-space: pre-line">{{ contact.notes }}</span>
                </data-row>

                <!-- The reverse index. This is what makes the directory an
                     inventory rather than just another form: every place this
                     contact is in use, one click away. -->
                <subheader-list-item title="Used On" icon="fa-link" :badge="links.length"/>
                <navigation-list-item
                    v-for="link in links"
                    :key="link.link_id"
                    :title="link.entity_title || '(untitled)'"
                    :subtitle="linkSubtitle(link)"
                    icon="fa-link"
                    @click="openHost(link)"
                />
                <container-list-item padded v-if="!links.length">
                    <p class="has-text-grey is-size-7 my-2">Not attached to anything yet.</p>
                </container-list-item>
            </card-list>
        </card-body>
    </div>
</template>

<script>
    import CardHeader from "../../TIER/components/CardHeader";
    import CardBody from "../../TIER/components/CardBody";
    import CardList from "../../TIER/components/CardList";
    import CardToolbar from "../../TIER/components/CardToolbar";
    import SubheaderListItem from "../../TIER/components/SubheaderListItem";
    import ContainerListItem from "../../TIER/components/ContainerListItem";
    import NavigationListItem from "../../TIER/components/NavigationListItem";
    import DataRow from "../../TIER/components/DataRow";
    import EntityTabs from "@/components/EntityTabs";
    import EntityTasksPane from "@/components/EntityTasksPane";
    import EntityNotesPane from "@/components/EntityNotesPane";
    import {client as http} from "../../http_client";
    import {typeLabel, roleLabel} from "@/components/contact_meta";

    // Which card owns each host type, and how to address it. Kept in step with
    // the entityTarget switch in cards/tasks/Details.vue -- same problem,
    // same answers.
    const HOST_CARDS = {
        'trip': link => ['trip', {tripId: link.entity_id}],
        'registration': link => ['registration', {registrationId: link.entity_id}],
        'trip-events': link => ['edit-event', {tripId: link.trip_id, dayId: link.day_id, eventId: link.entity_id}],
        'trip-addons': link => ['trip-addon', {addonId: link.entity_id}],
        'trip-hotels': link => ['edit-trip-hotel', {tripId: link.trip_id, hotelId: link.entity_id}],
        // A registration event is an override of a trip event when it carries
        // a day_event_id, otherwise a custom one -- different cards.
        'reg-events': link => link.day_event_id
            ? ['edit-override-event', {registrationId: link.registration_id, dayId: link.day_id, eventId: link.entity_id}]
            : ['edit-custom-event', {registrationId: link.registration_id, dayId: link.day_id, eventId: link.entity_id}],
        'reg-addons': link => ['edit-addon', {registrationId: link.registration_id, addonId: link.entity_id}],
        'reg-hotels': link => ['edit-reg-hotel', {registrationId: link.registration_id, hotelId: link.entity_id}],
        'flights': link => ['edit-flight', {registrationId: link.registration_id, flightId: link.entity_id}],
        'customer': link => ['customer', {userId: link.entity_id}],
        'lead': link => ['lead', {leadId: link.entity_id}]
    };

    export default {
        name: 'Contact',
        props: ['card', 'child', 'props'],
        components: {
            EntityNotesPane, EntityTasksPane, EntityTabs, NavigationListItem,
            ContainerListItem, SubheaderListItem, CardToolbar, CardList, CardBody, CardHeader, DataRow
        },
        data: function () {
            return {
                contact: {},
                links: [],
                tab: 'main',
                taskCount: 0,
                taskCompleted: 0,
                noteCount: 0
            };
        },
        computed: {
            typeLabel: function () {
                return typeLabel(this.contact.type);
            }
        },
        methods: {
            loadContact: function (force) {
                this.$emit('loading', true);
                http.get('/api/contacts/' + this.props.contactId, {force}).then(response => {
                    this.contact = response.data;
                    this.$emit('loading', false);
                });
                http.get('/api/contacts/' + this.props.contactId + '/links', {force}).then(response => {
                    this.links = response.data;
                });
            },
            linkSubtitle: function (link) {
                return [link.trip_title, roleLabel(link.role, this.contact.type)].filter(Boolean).join(' · ');
            },
            openHost: function (link) {
                const target = HOST_CARDS[link.entity_type];
                if (!target) return;
                const [component, props] = target(link);
                this.$openCard(component, props, this.card);
            },
            reload: function () {
                this.loadContact(true);
            }
        },
        watch: {
            'props.contactId': function () {
                this.loadContact(true);
            }
        },
        mounted() {
            this.loadContact();
        }
    };
</script>
